import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Share2,
  ExternalLink,
  MinusCircle,
  Plus,
  UserPlus,
} from 'lucide-react';
import type {
  AffiliateListQuery,
  AffiliateListResponse,
  AffiliateRow,
} from '@proptr/shared';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderLabel,
  TableCard,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { BioChannelsCell } from '../components/BioChannelsCell.js';
import { StatusBadge, statusLabel } from '../components/StatusBadge.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode, withDemoFlag } from '../lib/testMode.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

type StatusFilter = NonNullable<AffiliateListQuery['status']>;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All affiliates' },
  { value: 'enabled', label: 'Active affiliates' },
  { value: 'disabled', label: 'Disabled affiliates' },
];

// Same convention as the Proptree page: only render rows whose count > 0,
// in subtitle order. `all` is shown first as the headline number.
const STATUS_BREAKDOWN: {
  key: 'enabled' | 'disabled';
  label: string;
}[] = [
  { key: 'enabled', label: 'active' },
  { key: 'disabled', label: 'disabled' },
];

export function AffiliatePage() {
  const navigate = useNavigate();
  const sortBy: NonNullable<AffiliateListQuery['sort']> = 'createdAt';
  const order: NonNullable<AffiliateListQuery['order']> = 'desc';
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<AffiliateListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  // Create a fresh affiliate doc on the server, then navigate to its
  // settings page so the manager can fill in the remaining fields. The
  // server enforces the quota; we just surface its error here if it
  // refuses the create.
  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await client.proptrees.createAffiliate();
      navigate(`/affiliate/${created._id}/settings`);
    } catch (err) {
      setCreateError((err as Error).message);
      setCreating(false);
    }
  };

  // Bumped by row-menu actions (Enable/Disable) so the list — and the
  // quota card fed by its meta — refetches after a status change.
  const [refreshTick, setRefreshTick] = React.useState(0);
  const refresh = React.useCallback(() => setRefreshTick((t) => t + 1), []);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.proptrees
      .listAffiliates({ status, sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [status, sortBy, order, page, pageSize, refreshTick]);

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;
  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === status)?.label ?? 'Affiliates';

  // Quota left = `enterprise.limitProptreeAmount.value` − enabled affiliate
  // count. Mirrors enterprise-app/src/pages/agent/listProptrees.js:1067-1084.
  // `null` means the API didn't return a quota (no `limitProptreeAmount`
  // on the enterprise doc).
  const quota = meta?.quota ?? null;
  const enabled = meta?.counts.enabled ?? 0;
  const remaining = quota === null ? null : Math.max(0, quota - enabled);

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Affiliate
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              {meta ? (
                <>
                  <span className="text-foreground">{meta.counts.all}</span>{' '}
                  affiliate{meta.counts.all === 1 ? '' : 's'} in organization
                  {STATUS_BREAKDOWN.map(({ key, label }) => {
                    const n = meta.counts[key];
                    if (n === 0) return null;
                    return (
                      <React.Fragment key={key}>
                        <span className="mx-2 text-muted-foreground/60">·</span>
                        <span className="text-foreground">{n}</span> {label}
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                'Loading…'
              )}
            </p>
          </div>

          <NewAffiliateCard
            remaining={remaining}
            quota={quota}
            used={enabled}
            onCreate={handleCreate}
            creating={creating}
            createError={createError}
          />
        </div>

        <TableCard
          minWidth={960}
          busy={refreshing && data !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
            <table
              className={cn(
                'w-full border-collapse',
                // Stretch only when the body has no rows, so the empty /
                // loading state can centre. With rows present this would
                // divide the leftover height across them.
                (!items || items.length === 0) && 'flex-1',
              )}
            >
              <thead>
                <tr className="border-b border-[hsl(var(--hairline))]">
                  <th className="whitespace-nowrap px-5 py-3 text-left align-middle font-normal first:pl-5 last:pr-5">
                    <div className="flex items-center gap-3">
                      <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="mono-label group inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2.5 py-1 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                        >
                          <span>{statusLabel}</span>
                          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="min-w-[16rem]"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <DropdownMenuItem
                            key={opt.value}
                            onSelect={() => {
                              setStatus(opt.value);
                              setPage(1);
                            }}
                            className={cn(
                              'flex items-center justify-between gap-4',
                              opt.value === status && 'text-foreground',
                            )}
                          >
                            <span>{opt.label}</span>
                            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                              {opt.value === 'all'
                                ? meta?.counts.all ?? 0
                                : opt.value === 'enabled'
                                  ? meta?.counts.enabled ?? 0
                                  : meta?.counts.disabled ?? 0}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  </th>
                  {[
                    'Link in bio',
                    'Status',
                    'Active buttons',
                    'Page views',
                    'Button clicks',
                    'Referral clicks',
                    'Created',
                  ].map((label) => (
                    <th
                      key={label}
                      className="whitespace-nowrap px-5 py-3 text-left align-middle font-normal first:pl-5 last:pr-5"
                    >
                      <HeaderLabel>{label}</HeaderLabel>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan={8} className="h-full">
                      <TableMessage tone="destructive">{error}</TableMessage>
                    </td>
                  </tr>
                ) : items === null ? (
                  <tr>
                    <td colSpan={8} className="h-full">
                      <TableLoading />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="h-full">
                      <EmptyState
                        icon={Share2}
                        title="No affiliates yet"
                        description="Affiliate proptrees are standalone pages for partners and campaigns. Create one and it shows up here."
                      />
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <AffiliateRowEl
                      key={row._id}
                      row={row}
                      onChanged={refresh}
                    />
                  ))
                )}
              </tbody>
            </table>
        </TableCard>
      </div>
    </AppShell>
  );
}

// Compact "Create new affiliate" card shown next to the page title.
// Shows the affiliate allowance as numbers (used / quota, how many
// left) and turns into a warning once the allowance is spent. Quota
// comes from the subscription — plan's included affiliates plus any
// boost pack — so this is also where users learn they need to upgrade.
function NewAffiliateCard({
  remaining,
  quota,
  used,
  onCreate,
  creating,
  createError,
}: {
  remaining: number | null;
  quota: number | null;
  used: number;
  onCreate: () => void;
  creating: boolean;
  createError: string | null;
}) {
  // `quota === null` means the agency has no affiliate allowance on
  // record (no admin override, no plan) — treat it the same as a plan
  // with zero affiliates: point at billing instead of offering a Create
  // that implies the account already includes affiliates.
  const noPlan = quota === 0 || quota === null;
  const full = quota !== null && quota > 0 && remaining === 0;
  const warn = noPlan || full;
  const disabled = remaining === 0 || creating;

  const subtitle = createError
    ? createError
    : quota === null
      ? 'No affiliates on this account yet — choose a plan in billing to add them.'
      : noPlan
        ? 'Your plan includes no affiliates — upgrade or add a boost in billing.'
        : full
          ? 'Limit reached — add a boost in billing, or disable an affiliate.'
          : `${remaining} of ${quota} still available`;

  return (
    <Card
      className={cn(
        'flex w-[380px] items-center gap-3 px-4 py-3',
        warn && 'border-warning/50 bg-warning/5',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-sans text-[13px] font-semibold leading-tight text-foreground">
            New affiliate
          </span>
          {quota !== null && (
            <span
              className={cn(
                'mono-label inline-flex items-center rounded-full border px-2 py-0.5',
                warn
                  ? 'border-warning/40 bg-warning/10 text-warning'
                  : 'border-border bg-muted text-muted-foreground',
              )}
            >
              {used} / {quota} used
            </span>
          )}
        </div>
        <p
          className={cn(
            'mt-1 font-sans text-[11px] leading-snug',
            createError
              ? 'text-destructive'
              : warn
                ? 'text-warning'
                : 'text-muted-foreground',
          )}
        >
          {warn && !createError ? (
            <>
              <AlertTriangle className="mr-1 inline h-3 w-3 align-[-2px]" />
              {subtitle}
            </>
          ) : (
            subtitle
          )}
        </p>
      </div>
      {warn && !createError ? (
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to="/billing">Billing</Link>
        </Button>
      ) : isTestMode() ? (
        <Tooltip
          label="Can't create affiliate in test mode"
          className="cursor-not-allowed"
        >
          <Button size="sm" variant="outline" disabled>
            <Plus />
            Create
          </Button>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={onCreate}
          disabled={disabled}
        >
          <Plus />
          {creating ? 'Creating…' : 'Create'}
        </Button>
      )}
    </Card>
  );
}

function RowActionsMenu({
  row,
  onChanged,
}: {
  row: AffiliateRow;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  // Clone and enable/disable are real mutations — locked in test mode
  // (the fixture layer only intercepts GETs).
  const testMode = isTestMode();
  const isEnabled = row.status === 'enabled';
  const isDisabled = row.status === 'disabled';
  const publicSlug = row.alias || row.shortId;
  const [cloneOpen, setCloneOpen] = React.useState(false);
  const [statusBusy, setStatusBusy] = React.useState(false);

  const handleOpen = () => {
    if (!publicSlug) return;
    window.open(
      withDemoFlag(`${PUBLIC_URL}/${encodeURIComponent(publicSlug)}`),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleEdit = () => {
    navigate(`/affiliate/${row._id}/settings`);
  };

  // Flip status, then ask the page to refetch (rows + quota card both
  // come from the list response). Enable can 409 on a full quota — the
  // server message says to disable another affiliate first.
  const handleStatus = async (next: 'enabled' | 'disabled') => {
    if (statusBusy) return;
    setStatusBusy(true);
    try {
      await client.proptrees.setAffiliateStatus(row._id, next);
      onChanged();
    } catch (err) {
      window.alert((err as Error).message);
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
          aria-label="Affiliate actions"
        >
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[14rem]">
        <DropdownMenuItem onSelect={handleOpen} disabled={!publicSlug}>
          <span className="flex-1">Open Proptree</span>
          <ExternalLink />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleEdit}>
          <span className="flex-1">Edit</span>
          <Edit3 />
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => setCloneOpen(true)}
          disabled={testMode}
        >
          <span className="flex-1">Clone</span>
          <UserPlus />
        </DropdownMenuItem>
        {(isEnabled || isDisabled) && <DropdownMenuSeparator />}
        {isDisabled && (
          <DropdownMenuItem
            onSelect={() => void handleStatus('enabled')}
            disabled={statusBusy || testMode}
            className="text-emerald-600 focus:text-emerald-700 [&_svg]:text-emerald-600"
          >
            <span className="flex-1">Enable</span>
            <CheckCircle2 />
          </DropdownMenuItem>
        )}
        {isEnabled && (
          <DropdownMenuItem
            onSelect={() => void handleStatus('disabled')}
            disabled={statusBusy || testMode}
            className="text-destructive focus:text-destructive [&_svg]:text-destructive"
          >
            <span className="flex-1">Disable</span>
            <MinusCircle />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    <CloneAffiliateModal
      open={cloneOpen}
      onClose={() => setCloneOpen(false)}
      sourceId={row._id}
      sourceLabel={
        row.profileCompany ||
        [row.profileFirstName, row.profileSurName].filter(Boolean).join(' ') ||
        row.alias ||
        row.shortId ||
        'affiliate'
      }
    />
    </>
  );
}

// Modal: collects the new alias, checks availability against the
// proptree-wide alias index, and clones the source affiliate into a
// fresh doc via POST /affiliates/:id/clone. Mirrors the AddProptree-
// ButtonModal scaffold (backdrop + scaled-in card + Escape/click-out
// close + body-scroll lock) so the chrome feels consistent.
function CloneAffiliateModal({
  open,
  onClose,
  sourceId,
  sourceLabel,
}: {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  sourceLabel: string;
}) {
  const navigate = useNavigate();
  const [alias, setAlias] = React.useState('');
  const [status, setStatus] = React.useState<
    | { kind: 'idle' }
    | { kind: 'invalid'; message: string }
    | { kind: 'submitting' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [shown, setShown] = React.useState(open);
  const [visible, setVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      setAlias('');
      setStatus({ kind: 'idle' });
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => {
          setVisible(true);
          inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const submit = async () => {
    const value = alias.trim();
    if (value === '' || !/^[a-z0-9-]+$/.test(value)) {
      setStatus({
        kind: 'invalid',
        message:
          'Only lowercase letters, digits, and dashes are allowed — no spaces.',
      });
      return;
    }
    setStatus({ kind: 'submitting' });
    try {
      // Availability check first — gives a focused error message in
      // the modal before we even try the clone POST.
      const { available } = await client.proptrees.checkAffiliateAlias({
        alias: value,
      });
      if (!available) {
        setStatus({
          kind: 'invalid',
          message: 'Already in use — pick another shortname.',
        });
        return;
      }
      const created = await client.proptrees.cloneAffiliate(sourceId, value);
      navigate(`/affiliate/${created._id}/settings`);
    } catch (err) {
      setStatus({ kind: 'error', message: (err as Error).message });
    }
  };

  if (!shown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            Clone affiliate
          </h2>
          <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
            Make a copy of <span className="font-semibold text-foreground">{sourceLabel}</span>{' '}
            with a new shortname.
          </p>
        </div>
        <div className="px-5 py-5">
          <Label htmlFor="clone-alias">Custom shortname</Label>
          <div className="mt-1 flex">
            <span className="inline-flex h-11 items-center rounded-l-md border border-r-0 border-border bg-muted px-3 font-mono text-[13px] text-muted-foreground">
              https://proptr.ee/
            </span>
            <Input
              id="clone-alias"
              ref={inputRef}
              value={alias}
              onChange={(e) => {
                setAlias(e.target.value);
                if (status.kind === 'invalid' || status.kind === 'error') {
                  setStatus({ kind: 'idle' });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && status.kind !== 'submitting') {
                  e.preventDefault();
                  void submit();
                }
              }}
              className="rounded-l-none bg-muted/30"
            />
          </div>
          {(status.kind === 'invalid' || status.kind === 'error') && (
            <p className="mt-2 font-sans text-[12px] text-destructive">
              {status.message}
            </p>
          )}
          <p className="mt-2 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Lowercase letters, numbers, and dashes only. Must be unique on
            proptr.ee.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] bg-muted/30 px-5 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={status.kind === 'submitting'}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={status.kind === 'submitting' || alias.trim() === ''}
          >
            {status.kind === 'submitting' ? 'Cloning…' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AffiliateRowEl({
  row,
  onChanged,
}: {
  row: AffiliateRow;
  onChanged: () => void;
}) {
  const displayName =
    row.profileCompany ||
    [row.profileFirstName, row.profileSurName].filter(Boolean).join(' ') ||
    row.alias ||
    '—';
  const isActive = row.status === 'enabled';

  return (
    <tr className="border-b border-[hsl(var(--hairline))] last:border-b-0 transition-colors hover:bg-muted/40">
      <Cell>
        <div className="flex items-center gap-3">
          <RowActionsMenu row={row} onChanged={onChanged} />
          <ProfileAvatar image={row.profileImage} name={displayName} />
          <div className="min-w-0">
            <div className="truncate font-sans text-[14px] font-medium text-foreground">
              {displayName}
            </div>
            {(row.alias || row.shortId) && (
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                /{row.alias ?? row.shortId}
              </div>
            )}
          </div>
        </div>
      </Cell>
      <Cell>
        <BioChannelsCell channels={row.linkInBio ?? null} />
      </Cell>
      <Cell>
        <StatusBadge
          label={statusLabel(row.status)}
          tone={isActive ? 'success' : 'muted'}
        />
      </Cell>
      <Cell>
        <Mono
          value={`${row.activeButtons}/${row.buttonsCount}`}
          dim={row.buttonsCount === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={row.pageViews.toLocaleString()}
          dim={row.pageViews === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={row.buttonClicks.toLocaleString()}
          dim={row.buttonClicks === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={row.referralClicks.toLocaleString()}
          dim={row.referralClicks === 0}
        />
      </Cell>
      <Cell>
        <Mono value={formatDate(row.createdAt)} dim={!row.createdAt} />
      </Cell>
    </tr>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-5 py-4 align-middle first:pl-5 last:pr-5">{children}</td>
  );
}

function Mono({ value, dim }: { value: string; dim?: boolean }) {
  return (
    <div
      className={cn(
        'whitespace-nowrap font-mono text-[12px] font-medium tabular-nums text-foreground/80',
        dim && 'text-muted-foreground',
      )}
    >
      {value}
    </div>
  );
}

function ProfileAvatar({
  image,
  name,
}: {
  image: string | null;
  name: string;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-9 w-9 shrink-0 rounded-md bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-[11px] font-bold uppercase">
      {initials(name)}
    </div>
  );
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
